import { useCallback, useEffect, useRef, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Divider, Box, Loader, Tooltip } from '@mantine/core'
import { useDisclosure, useLocalStorage } from '@mantine/hooks'
import Draggable from 'react-draggable'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import {
  CallStatus,
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import {
  GET_ACTIVE_CALL,
  COMPLETE_ACTIVE_CALL,
} from 'src/graphql/activityLogs.graphql'
import { GET_TWILIO_ACCESS_TOKEN } from 'src/graphql/twilio.graphql'
import {
  OUTBOUND_CALLER_ID_SOURCE_LABEL,
  OutboundCallerIdSource,
} from 'src/graphql/types/verifiedCallerIds'
import { GET_MY_OUTBOUND_CALLER_ID } from 'src/graphql/verifiedCallerIds.graphql'
import { PLAY_VOICEMAIL_IN_ACTIVE_CALL } from 'src/graphql/voiceMailTemplate.graphql'
import IconDialpad from 'src/icons/IconDialpad'
import IconHangup from 'src/icons/IconHangup'
import IconMicrophone from 'src/icons/IconMicrophone'
import IconPhoneCall from 'src/icons/IconPhoneCall'
import IconPlay from 'src/icons/IconPlay'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'
import { cn } from 'src/utils'

import VoiceMailTemplateSelect from '../../Combobox/VoiceMailTemplateSelect/VoiceMailTemplateSelect'
import MicPermissionDeniedModal from '../MicPermissionDeniedModal/MicPermissionDeniedModal'
import { toast } from '../Toast/Toast'

import TransferCallPopover from './TransferCallPopover'

const onError = () => {
  toast('Unable to play voicemail, please try again.', 'error')
}

type CompleteActiveCallResult = {
  activityLogs: { activityLogId: string }[]
}

const ActiveCallToast = ({
  toastId,
  onComplete,
}: {
  toastId: string
  onComplete: () => Promise<CompleteActiveCallResult | undefined>
}) => {
  const [isCompleting, setIsCompleting] = useState(false)

  const handleClick = async () => {
    if (isCompleting) {
      return
    }

    setIsCompleting(true)

    try {
      const result = await onComplete()

      toast.dismiss(toastId)

      if (result?.activityLogs?.length) {
        toast(
          'All active calls are completed. Please try calling again.',
          'success'
        )
      } else {
        toast(
          'No lingering active calls were found. Please try calling again.',
          'success'
        )
      }
    } catch (error) {
      console.error(error)
      toast('Unable to complete active calls. Please try again.', 'error')
    } finally {
      setIsCompleting(false)
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md bg-white p-4 text-sm text-doubleNickel-gray-700 shadow-lg">
      <p>
        There is an active call. If this is incorrect, click below to complete
        any lingering calls before trying again.
      </p>
      <button
        type="button"
        onClick={handleClick}
        className="self-start rounded-md bg-doubleNickel-brand-500 px-3 py-1 font-semibold text-white hover:bg-doubleNickel-brand-600 disabled:cursor-not-allowed disabled:bg-doubleNickel-gray-300"
        disabled={isCompleting}
      >
        {isCompleting ? 'Completing…' : 'Complete active calls'}
      </button>
    </div>
  )
}

const CallCancelButton = ({
  onCall,
  onCancel,
  isConnecting = false,
}: {
  onCall: () => void
  onCancel: () => void
  isConnecting?: boolean
}) => {
  return (
    <div className="absolute bottom-0 right-0 top-0 flex w-[20%] flex-col border-l">
      <Box
        component="button"
        type="button"
        onClick={onCall}
        disabled={isConnecting}
        aria-disabled={isConnecting}
        aria-label={isConnecting ? 'Connecting call' : 'Call'}
        className={cn(
          'flex flex-1 flex-row items-center justify-center gap-1 border-0 bg-transparent text-sm font-semibold',
          isConnecting
            ? 'cursor-not-allowed text-doubleNickel-gray-400'
            : 'cursor-pointer text-doubleNickel-brand-500 hover:bg-doubleNickel-brand-25'
        )}
      >
        {isConnecting ? (
          <Loader color="gray" type="dots" size="sm" />
        ) : (
          <>
            <IconPhoneCall className="h-4 fill-none stroke-doubleNickel-brand-500" />
            Call
          </>
        )}
      </Box>
      <Divider className="border-b-1 w-full border-doubleNickel-gray-200" />
      <Box
        component="button"
        type="button"
        onClick={onCancel}
        disabled={isConnecting}
        aria-disabled={isConnecting}
        aria-label="Cancel"
        className={cn(
          'flex flex-1 flex-col justify-center border-0 bg-transparent text-center text-sm font-semibold',
          isConnecting
            ? 'cursor-not-allowed text-doubleNickel-gray-400'
            : 'cursor-pointer text-doubleNickel-gray-600 hover:bg-doubleNickel-brand-25'
        )}
      >
        Cancel
      </Box>
    </div>
  )
}

const HangupMuteButton = ({ onHangup, onMute }) => {
  const [isMuted, setIsMuted] = useState(false)

  const handleMute = () => {
    setIsMuted(!isMuted)
    onMute()
  }
  return (
    <div className="flex flex-row gap-4">
      <ActionIcon
        onClick={handleMute}
        radius="xl"
        variant="outline"
        classNames={{
          root: `border ${
            isMuted
              ? 'border-doubleNickel-error-500'
              : 'border-doubleNickel-gray-400'
          }`,
        }}
      >
        <IconMicrophone
          className={`fill-none ${
            isMuted
              ? 'stroke-doubleNickel-error-500'
              : 'stroke-doubleNickel-gray-400'
          } `}
        />
      </ActionIcon>
      <ActionIcon onClick={onHangup} radius="xl" variant="filled" color="red">
        <IconHangup fill="none" stroke="white" />
      </ActionIcon>
    </div>
  )
}

const KEYPAD_KEYS: [string, string][] = [
  ['1', ''],
  ['2', 'ABC'],
  ['3', 'DEF'],
  ['4', 'GHI'],
  ['5', 'JKL'],
  ['6', 'MNO'],
  ['7', 'PQRS'],
  ['8', 'TUV'],
  ['9', 'WXYZ'],
  ['*', ''],
  ['0', '+'],
  ['#', ''],
]

const PhoneKeypad = ({ call }) => {
  const [dialedDigits, setDialedDigits] = useState('')

  const handleDigitPress = (digit: string) => {
    if (!call) return
    call.sendDigits(digit)
    setDialedDigits((prev) => prev + digit)
  }

  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-doubleNickel-gray-200 pt-2.5">
      {dialedDigits && (
        <div className="flex items-center justify-center gap-1.5">
          <span className="font-mono text-sm font-medium tracking-widest text-doubleNickel-gray-700">
            {dialedDigits}
          </span>
          <button
            type="button"
            onClick={() => setDialedDigits('')}
            className="text-[10px] text-doubleNickel-gray-500 hover:text-doubleNickel-error-500"
          >
            Clear
          </button>
        </div>
      )}
      <div className="mx-auto grid max-w-[180px] grid-cols-3 gap-1.5">
        {KEYPAD_KEYS.map(([digit, sub]) => (
          <button
            key={digit}
            type="button"
            onClick={() => handleDigitPress(digit)}
            className="flex h-[52px] w-[52px] flex-col items-center justify-center rounded-full bg-doubleNickel-gray-200 transition-all hover:bg-doubleNickel-gray-300 active:scale-95 active:bg-doubleNickel-gray-400"
          >
            <span className="text-base font-semibold leading-none text-doubleNickel-gray-700">
              {digit}
            </span>
            {sub && (
              <span className="mt-0.5 text-[8px] uppercase tracking-wider text-doubleNickel-gray-500">
                {sub}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

const CallStatusDisplay = ({ callState, toNumber, currentTime }) => {
  return (
    <>
      <p className="text-xs font-medium capitalize text-doubleNickel-gray-700">
        {callState === CallStatus.INITIATED
          ? `Calling ${formatInternationalPhoneNumber(toNumber)}`
          : callState === CallStatus.RINGING
          ? `Ringing: ${formatInternationalPhoneNumber(toNumber)}`
          : callState === CallStatus.IN_PROGRESS
          ? `${formatTime(currentTime)}`
          : callState === CallStatus.NO_ANSWER
          ? `No answer`
          : callState === CallStatus.COMPLETED
          ? `Completed`
          : callState === CallStatus.BUSY
          ? `Busy`
          : callState === CallStatus.LEAVING_VOICEMAIL
          ? `No answer - Leaving voicemail`
          : callState === CallStatus.LEFT_VOICEMAIL
          ? `No answer - Voicemail left`
          : callState === CallStatus.FAILED
          ? `Call failed, please check the phone number`
          : null}
      </p>
    </>
  )
}

const LeaveVoicemailButton = ({
  showPlayVoicemailButton,
  onPlayVoiceMail,
  phoneDeviceContext,
}) => {
  if (!showPlayVoicemailButton) return null

  return (
    <div className="flex flex-row items-center gap-1">
      <Box
        onClick={() => onPlayVoiceMail()}
        className="rounded-lg hover:bg-doubleNickel-brand-25"
      >
        <IconPlay className="h-3 fill-none stroke-doubleNickel-brand-500" />
      </Box>
      {phoneDeviceContext.callState === CallStatus.LEAVING_VOICEMAIL ? (
        <>
          <Loader color="blue" type="bars" size={12} />
          <Loader color="blue" type="bars" size={12} />
        </>
      ) : (
        <div className="text-xs font-medium text-doubleNickel-gray-500">
          Play voicemail
        </div>
      )}
    </div>
  )
}

const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0'
  )}`
}

export const PhoneDeviceModal = () => {
  const phoneDeviceContext = usePhoneDevice()
  const dispatch = usePhoneDeviceDispatch()
  const { currentUser } = useAuth()
  const [startTime, setStartTime] = useState(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const userScopedKey =
    currentUser?.userId != null
      ? `dn.dialer.voicemailTemplateId.${currentUser.userId}`
      : 'dn.dialer.voicemailTemplateId.__no_user__'
  const [selectedVoiceMailTemplateId, setSelectedVoiceMailTemplateId] =
    useLocalStorage<string | null>({
      key: userScopedKey,
      defaultValue: null,
      getInitialValueInEffect: false,
    })
  const [openedInstructions, { open: openInstructionsModal, close }] =
    useDisclosure(false)
  const [keypadOpen, setKeypadOpen] = useState(false)

  const [getAccessToken] = useLazyQuery(GET_TWILIO_ACCESS_TOKEN)
  const [getActiveCall] = useLazyQuery(GET_ACTIVE_CALL)
  // Fetched fresh each time the dialer opens: which number the applicant will
  // see (verified recruiter/company phone, or the Double Nickel number).
  const [getMyOutboundCallerId, myOutboundCallerIdResult] = useLazyQuery(
    GET_MY_OUTBOUND_CALLER_ID,
    { fetchPolicy: 'network-only' }
  )
  const outboundCallerId = myOutboundCallerIdResult?.data?.myOutboundCallerId
  const [completeActiveCallMutation] = useMutation(COMPLETE_ACTIVE_CALL)
  const [playVoiceMail, { loading: loadingVoicemail }] = useMutation(
    PLAY_VOICEMAIL_IN_ACTIVE_CALL,
    {
      onCompleted: ({ playVoiceMailInActiveCall }) => {
        if (!playVoiceMailInActiveCall.sent) {
          toast('There was an error trying to sending the voicemail', 'warning')
        }
      },
      onError,
    }
  )

  const [lastActivityLogId, setLastActivityLogId] = useState(null)
  // After a transfer the recruiter's own leg drops, and the reducer drops the
  // teammate's callbacks for that log; the dialer stays idle until the next
  // call brings a new activity log.
  const isTransferredCall =
    !!phoneDeviceContext?.transferredActivityLogId &&
    phoneDeviceContext?.activityLogId ===
      phoneDeviceContext?.transferredActivityLogId
  // Synchronous guard: React state alone can't block a second click that
  // arrives before the re-render from setIsConnecting(true).
  const isConnectingRef = useRef(false)
  const [isConnecting, setIsConnecting] = useState(false)

  const clearConnecting = useCallback(() => {
    isConnectingRef.current = false
    setIsConnecting(false)
  }, [])

  const handleCallSetupFailure = useCallback(
    (error) => {
      console.error(error)
      setLastActivityLogId(null)
      clearConnecting()
      dispatch({
        type: PhoneDeviceActionType.SET_CALL_STATE,
        payload: {
          call: null,
          callState: CallStatus.FAILED,
        },
      })
      toast('Call failed before connecting. Please try again.', 'error')
    },
    [dispatch, clearConnecting]
  )

  // Timer to update call duration
  useEffect(() => {
    let timer = null
    if (
      phoneDeviceContext?.callState === CallStatus.IN_PROGRESS &&
      startTime &&
      phoneDeviceContext?.phoneDevice?.isBusy
    ) {
      timer = setInterval(() => {
        setElapsedTime(new Date().getTime() - startTime)
      }, 1000)
    } else {
      clearInterval(timer)
    }

    return () => clearInterval(timer)
  }, [phoneDeviceContext, startTime])

  // Once Twilio reports the device is busy, Call/Cancel swap to Hangup —
  // clear the connecting lock so a later retry after hangup works.
  useEffect(() => {
    if (phoneDeviceContext?.phoneDevice?.isBusy) {
      clearConnecting()
    }
  }, [phoneDeviceContext?.phoneDevice?.isBusy, clearConnecting])

  /**
   * Use Effect to handle CallStatus changes.
   * Capture the active call activityLogId as soon as the call reaches an
   * active lifecycle state (initiated/ringing/in-progress), since websocket
   * events may arrive after initiated.
   * If the call is IN_PROGRESS and the phoneDevice is busy, set the startTime
   * to calculate the call duration.
   * If the call is in an end state and the lastActivityLogId is the same as
   * the current activityLogId, reset the startTime and elapsedTime. This is to
   * only change statuses and counters if this is the active call (or was).
   */
  useEffect(() => {
    // Capture activityLogId whenever the call is live (isBusy=true) and the
    // websocket has delivered a state describing an active or AMD-classified
    // call. Including LEAVING_VOICEMAIL / LEFT_VOICEMAIL here covers the AMD
    // race where the first websocket event we ever see is one of those
    // states (no INITIATED/RINGING/IN_PROGRESS arrived first). Without the
    // capture in that scenario, the cleanup guard `lastActivityLogId ===
    // activityLogId` would never match after disconnect, leaving the modal
    // stuck on stale LEFT_VOICEMAIL state.
    //
    // It is safe to capture during voicemail states only because the
    // cleanup useEffect gates AMD end states on `!isBusy` (see below) — the
    // earlier-PR concern about capturing-then-tearing-down a still-live
    // call no longer applies.
    const isActiveCallState =
      phoneDeviceContext?.callState === CallStatus.INITIATED ||
      phoneDeviceContext?.callState === CallStatus.RINGING ||
      phoneDeviceContext?.callState === CallStatus.IN_PROGRESS ||
      phoneDeviceContext?.callState === CallStatus.LEAVING_VOICEMAIL ||
      phoneDeviceContext?.callState === CallStatus.LEFT_VOICEMAIL

    if (
      phoneDeviceContext?.phoneDevice?.isBusy &&
      phoneDeviceContext?.activityLogId &&
      isActiveCallState &&
      !isTransferredCall &&
      lastActivityLogId !== phoneDeviceContext?.activityLogId
    ) {
      setLastActivityLogId(phoneDeviceContext?.activityLogId)
    }

    if (
      startTime === null &&
      phoneDeviceContext?.callState === CallStatus.IN_PROGRESS &&
      phoneDeviceContext?.phoneDevice?.isBusy
    ) {
      setStartTime(new Date().getTime())
    }

    // Lifecycle end states (COMPLETED, NO_ANSWER, BUSY, FAILED) are bound to
    // the actual call lifecycle on the server — cleanup runs as soon as the
    // websocket carries them. This matters for pre-answer endings where the
    // SDK may emit a `cancel` event instead of `disconnect`; the context only
    // listens for `disconnect`, so `isBusy` may not flip false without a
    // re-render. Trusting the server here avoids a stuck modal.
    const isLifecycleEndState = [
      CallStatus.COMPLETED,
      CallStatus.NO_ANSWER,
      CallStatus.BUSY,
      CallStatus.FAILED,
    ].includes(phoneDeviceContext?.callState)

    // AMD-classification end states report independently of the media
    // session. AMD in 'DetectMessageEnd' mode escalates `machine_start`
    // (LEAVING_VOICEMAIL) to `machine_end_*` (LEFT_VOICEMAIL) a few seconds
    // later — but for an IVR that AMD misclassified, the audio session is
    // still alive and the recruiter needs to keep pressing DTMF. Defer
    // teardown here until the SDK's `disconnect` event flips `isBusy` false.
    const isAMDEndState =
      phoneDeviceContext?.callState === CallStatus.LEFT_VOICEMAIL ||
      (phoneDeviceContext?.callState === CallStatus.LEAVING_VOICEMAIL &&
        selectedVoiceMailTemplateId != null)

    const shouldCleanup =
      lastActivityLogId === phoneDeviceContext?.activityLogId &&
      (isLifecycleEndState ||
        (isAMDEndState && !phoneDeviceContext?.phoneDevice?.isBusy))

    if (shouldCleanup) {
      setStartTime(null)
      setElapsedTime(0)
      setKeypadOpen(false)
      setTimeout(() => {
        dispatch({
          type: PhoneDeviceActionType.SET_CALL_STATE,
          payload: {
            callState: null,
          },
        })
      }, 1000)
    }
  }, [
    phoneDeviceContext?.callState,
    phoneDeviceContext?.activityLogId,
    lastActivityLogId,
    isTransferredCall,
    phoneDeviceContext?.phoneDevice?.isBusy,
    startTime,
  ])

  const onTransferred = useCallback(() => {
    setLastActivityLogId(null)
    setStartTime(null)
    setElapsedTime(0)
    setKeypadOpen(false)
    dispatch({
      type: PhoneDeviceActionType.SET_CALL_STATE,
      payload: {
        callState: null,
        transferredActivityLogId: phoneDeviceContext?.activityLogId,
      },
    })
  }, [phoneDeviceContext?.activityLogId, dispatch])

  const onCall = useCallback(async () => {
    // ENG-4552: recruiters double/triple-tap Call during the lag between
    // click and Twilio isBusy. Each connect() creates a real outbound leg.
    if (isConnectingRef.current || phoneDeviceContext?.phoneDevice?.isBusy) {
      return
    }

    isConnectingRef.current = true
    setIsConnecting(true)

    try {
      const { data: activeCallData } = await getActiveCall()
      if (activeCallData.getActiveCall) {
        clearConnecting()
        toast(
          'There is an active call, marking them as completed now. Please try again.',
          'warning'
        )
        await completeActiveCallMutation()
        return
      }

      // Update the token everytime it calls to prevent expiration errors.
      const { data } = await getAccessToken()
      const { accessToken } = data.getTwilioAccessToken
      phoneDeviceContext.phoneDevice.updateToken(accessToken)

      const call = await phoneDeviceContext.phoneDevice.connect({
        params: {
          To: phoneDeviceContext.phone,
          applicantId: phoneDeviceContext.applicantId,
          employeeId: currentUser.employeeId,
          ...(phoneDeviceContext.voeId && {
            voeId: phoneDeviceContext.voeId,
          }),
          ...(selectedVoiceMailTemplateId && {
            voiceMailTemplateId: selectedVoiceMailTemplateId,
          }),
        },
        rtcConstraints: {
          audio: {
            noiseSuppression: true,
            echoCancellation: true,
            autoGainControl: true,
          },
        },
      })

      dispatch({
        type: PhoneDeviceActionType.SET_CALL_STATE,
        payload: {
          call,
          callState: CallStatus.INITIATED,
        },
      })
      call.on('error', (error) => {
        if (error.name === 'PermissionDeniedError' && error.code === 31401) {
          openInstructionsModal()
        }
        handleCallSetupFailure(error)
      })
    } catch (error) {
      handleCallSetupFailure(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phoneDeviceContext,
    currentUser,
    selectedVoiceMailTemplateId,
    completeActiveCallMutation,
    handleCallSetupFailure,
    clearConnecting,
  ])

  const onCancel = useCallback(() => {
    dispatch({
      type: PhoneDeviceActionType.CLOSE_DEVICE_MODAL,
    })
  }, [])

  const onHangup = useCallback(() => {
    phoneDeviceContext.call.disconnect()
  }, [phoneDeviceContext])

  const onMute = useCallback(() => {
    phoneDeviceContext.call.mute(!phoneDeviceContext.call.isMuted())
  }, [phoneDeviceContext])

  const isEmployerCall = !!phoneDeviceContext.voeId

  useEffect(() => {
    if (phoneDeviceContext.openedDeviceModal && !isEmployerCall) {
      getMyOutboundCallerId()
    }
  }, [
    phoneDeviceContext.openedDeviceModal,
    isEmployerCall,
    getMyOutboundCallerId,
  ])

  const displayName = isEmployerCall
    ? phoneDeviceContext.companyName
    : phoneDeviceContext.applicantFullName

  const onApplicantNameClick = useCallback(() => {
    if (!phoneDeviceContext.applicantId) return
    navigate(
      routes.applicantDetails({
        id: phoneDeviceContext.applicantId,
        tab: 'application',
      })
    )
  }, [phoneDeviceContext])

  const showCallButton = !phoneDeviceContext?.phoneDevice?.isBusy
  const showHangupButton = phoneDeviceContext?.phoneDevice?.isBusy

  // LEAVING_VOICEMAIL / LEFT_VOICEMAIL accept either signal: isBusy (covers
  // the AMD-misclassified-IVR case where the media session is still live)
  // or the lastActivityLogId match (covers the normal flow's disconnect
  // gap, where Twilio drops the session before the LEFT_VOICEMAIL websocket
  // arrives). Without the OR, the normal flow flashes the voicemail-
  // template dropdown for the few hundred ms between the disconnect and
  // LEFT_VOICEMAIL transition.
  //
  // Active call states (INITIATED/RINGING/IN_PROGRESS) use the same isBusy
  // OR lastActivityLogId pattern (ENG-4552). Relying only on the activityLogId
  // match hides the call overlay when the websocket delivers a new
  // activityLogId before the capture effect runs — including when the
  // applicant answers (IN_PROGRESS) — which looks like the dialer reset
  // while the call is still live.
  //
  // Note: `lastActivityLogId` may be captured during voicemail states (see the capture effect above).
  // This display gating intentionally avoids relying solely on `activityLogMatches` while media is live
  // so the call overlay does not disappear during websocket/capture races.
  //
  const isActiveCallDisplayState = [
    CallStatus.INITIATED,
    CallStatus.RINGING,
    CallStatus.IN_PROGRESS,
  ].includes(phoneDeviceContext.callState)

  const isLifecycleEndDisplayState = [
    CallStatus.BUSY,
    CallStatus.NO_ANSWER,
    CallStatus.LEFT_VOICEMAIL,
    CallStatus.FAILED,
  ].includes(phoneDeviceContext.callState)

  const activityLogMatches =
    lastActivityLogId === phoneDeviceContext?.activityLogId

  const showDisplay =
    isConnecting ||
    (!isTransferredCall &&
      ((isActiveCallDisplayState &&
        (phoneDeviceContext?.phoneDevice?.isBusy || activityLogMatches)) ||
        (isLifecycleEndDisplayState && activityLogMatches) ||
        ((phoneDeviceContext?.callState === CallStatus.LEAVING_VOICEMAIL ||
          phoneDeviceContext?.callState === CallStatus.LEFT_VOICEMAIL) &&
          (phoneDeviceContext?.phoneDevice?.isBusy || activityLogMatches))))

  const showSelectVoiceMail = !showDisplay

  const onSelectVoiceMailTemplate = useCallback(
    (selectedVoiceMailTemplateId) => {
      if (!currentUser?.userId) return
      setSelectedVoiceMailTemplateId(selectedVoiceMailTemplateId)
    },
    [currentUser?.userId, setSelectedVoiceMailTemplateId]
  )

  const onClearVoiceMailTemplate = useCallback(() => {
    if (!currentUser?.userId) return
    setSelectedVoiceMailTemplateId(null)
  }, [currentUser?.userId, setSelectedVoiceMailTemplateId])

  const onPlayVoiceMail = useCallback(() => {
    if (selectedVoiceMailTemplateId) {
      playVoiceMail({
        variables: {
          input: {
            voiceMailTemplateId: selectedVoiceMailTemplateId,
            activityLogId: phoneDeviceContext?.activityLogId,
          },
        },
      })
    }
  }, [
    selectedVoiceMailTemplateId,
    phoneDeviceContext?.activityLogId,
    phoneDeviceContext?.call?.parameters?.CallSid,
    playVoiceMail,
  ])

  const showPlayVoicemailButton =
    selectedVoiceMailTemplateId &&
    (phoneDeviceContext?.callState === CallStatus.IN_PROGRESS ||
      phoneDeviceContext?.callState === CallStatus.LEAVING_VOICEMAIL) &&
    !isTransferredCall &&
    !loadingVoicemail

  // A transfer redirects the applicant's leg, so it only exists once both
  // parties are bridged, not while ringing or leaving a voicemail.
  const showTransfer =
    phoneDeviceContext?.callState === CallStatus.IN_PROGRESS &&
    !!phoneDeviceContext?.activityLogId &&
    !isTransferredCall

  // ENG-4087: Show keypad always when call media is live
  const showKeypad =
    phoneDeviceContext?.phoneDevice?.isBusy &&
    ![
      CallStatus.COMPLETED,
      CallStatus.NO_ANSWER,
      CallStatus.BUSY,
      CallStatus.FAILED,
    ].includes(phoneDeviceContext?.callState)

  return (
    <Draggable
      defaultClassName={`${
        phoneDeviceContext.openedDeviceModal
          ? 'bg-doubleNickel-gray-100 min-h-[72px] w-[400px] absolute right-5 top-5 rounded-lg p-4 z-[250] shadow-md'
          : 'hidden'
      }`}
    >
      <div>
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1 text-sm font-semibold text-doubleNickel-brand-500">
            <Box
              onClick={onApplicantNameClick}
              className={cn(
                phoneDeviceContext.applicantId &&
                  'cursor-pointer hover:underline'
              )}
            >
              {displayName}
            </Box>
            {isEmployerCall && (
              <span className="text-xs font-normal text-doubleNickel-gray-500">
                Verification Call
              </span>
            )}
            {!isEmployerCall &&
              outboundCallerId?.phoneNumber &&
              outboundCallerId.source !==
                OutboundCallerIdSource.DOUBLE_NICKEL && (
                <span className="text-xs font-normal text-doubleNickel-gray-500">
                  Calling from{' '}
                  {OUTBOUND_CALLER_ID_SOURCE_LABEL[outboundCallerId.source]} ·{' '}
                  {formatInternationalPhoneNumber(outboundCallerId.phoneNumber)}
                </span>
              )}
            {showDisplay && (
              <div className="flex flex-row items-center gap-4">
                {/* Prefer Connecting… whenever a new dial is in flight, even if
                    callState is still a stale terminal/in-progress value from
                    the previous call (hangup clears isBusy before the 1s
                    websocket cleanup nulls callState). */}
                {isConnecting ? (
                  <p className="text-xs font-medium text-doubleNickel-gray-700">
                    Connecting…
                  </p>
                ) : (
                  <CallStatusDisplay
                    callState={phoneDeviceContext.callState}
                    toNumber={phoneDeviceContext.phone}
                    currentTime={elapsedTime}
                  />
                )}
                {(isConnecting ||
                  phoneDeviceContext.callState === CallStatus.INITIATED ||
                  phoneDeviceContext.callState === CallStatus.RINGING) && (
                  <Loader color="blue" type="dots" size="sm" />
                )}
              </div>
            )}
            {showSelectVoiceMail && (
              <VoiceMailTemplateSelect
                selectedVoiceMailTemplateId={selectedVoiceMailTemplateId}
                onClear={onClearVoiceMailTemplate}
                onSelect={onSelectVoiceMailTemplate}
              />
            )}
            <LeaveVoicemailButton
              showPlayVoicemailButton={showPlayVoicemailButton}
              onPlayVoiceMail={onPlayVoiceMail}
              phoneDeviceContext={phoneDeviceContext}
            />
          </div>
          {showCallButton && (
            <CallCancelButton
              onCall={onCall}
              onCancel={onCancel}
              isConnecting={isConnecting}
            />
          )}
          {showHangupButton && (
            <div className="flex flex-row items-center gap-4">
              {showTransfer && (
                <TransferCallPopover
                  activityLogId={phoneDeviceContext.activityLogId}
                  applicantId={phoneDeviceContext.applicantId}
                  currentEmployeeId={currentUser?.employeeId}
                  onTransferred={onTransferred}
                />
              )}
              {showKeypad && (
                <Tooltip label={keypadOpen ? 'Hide keypad' : 'Show keypad'}>
                  <ActionIcon
                    onClick={() => setKeypadOpen((o) => !o)}
                    radius="xl"
                    variant="outline"
                    aria-label={keypadOpen ? 'Hide keypad' : 'Show keypad'}
                    classNames={{
                      root: `border ${
                        keypadOpen
                          ? 'border-doubleNickel-brand-500'
                          : 'border-doubleNickel-gray-400'
                      }`,
                    }}
                  >
                    <IconDialpad
                      className={`h-4 w-4 ${
                        keypadOpen
                          ? 'text-doubleNickel-brand-500'
                          : 'text-doubleNickel-gray-400'
                      }`}
                    />
                  </ActionIcon>
                </Tooltip>
              )}
              <HangupMuteButton onHangup={onHangup} onMute={onMute} />
            </div>
          )}
          <MicPermissionDeniedModal
            isOpen={openedInstructions}
            onClose={close}
          />
        </div>
        {keypadOpen && showKeypad && (
          <PhoneKeypad call={phoneDeviceContext.call} />
        )}
      </div>
    </Draggable>
  )
}
